import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, User } from "lucide-react";

export default function Chat() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const scrollRef = useRef();

  const { data: messages = [] } = useQuery({
    queryKey: ["chat-messages"],
    queryFn: () => api.get("/messages").then(r => r.data.reverse()),
    refetchInterval: 5000,
  });

  const sendMutation = useMutation({
    mutationFn: (text) => api.post("/messages", { text }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["chat-messages"] });
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    sendMutation.mutate(text);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] lg:h-screen bg-white">
      <header className="p-4 border-b border-border bg-white flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-extrabold text-navy text-primary">Chat del Patio</h1>
          <p className="text-xs text-muted-foreground">Comunicación en tiempo real entre guardias y supervisores.</p>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-secondary/30">
        {messages.map((m) => {
          const isMe = m.user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] p-3 rounded-2xl ${
                isMe ? "bg-navy text-white rounded-tr-none" : "bg-white border border-border text-navy rounded-tl-none"
              }`}>
                {!isMe && <p className="text-[10px] font-bold uppercase tracking-wider mb-1 opacity-60">{m.user_name}</p>}
                <p className="text-sm">{m.text}</p>
                <p className={`text-[9px] mt-1 text-right ${isMe ? "text-white/60" : "text-muted-foreground"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSend} className="p-4 border-t border-border bg-white flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribe un mensaje..."
          className="input flex-1 h-12"
        />
        <button
          type="submit"
          disabled={!text.trim() || sendMutation.isPending}
          className="w-12 h-12 bg-navy text-white flex items-center justify-center rounded-full hover:bg-navy-hover disabled:opacity-50 transition-colors"
        >
          <Send size={20} />
        </button>
      </form>
    </div>
  );
}
